import { useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Card, Row, Col, Form, Button, Badge } from "react-bootstrap";
import {
    FaUpload,
    FaTrash,
    FaPhone,
    FaEnvelope,
    FaMapMarkerAlt,
    FaUserAlt,
} from "react-icons/fa";
import moment from "moment-timezone";
import { getCurrentUser } from "../../common/utils";
import styles from "./VisualizadorJsonJivo.module.css";

const TZ = "America/Sao_Paulo";

const formatTimestamp = (epochSeconds) => {
    if (!epochSeconds) return "-";
    return moment.unix(epochSeconds).tz(TZ).format("DD/MM/YYYY HH:mm");
};

const formatTime = (epochSeconds) => {
    if (!epochSeconds) return "";
    return moment.unix(epochSeconds).tz(TZ).format("HH:mm");
};

const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return "-";
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m < 60) return `${m}m ${s}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
};

const isAgentMessage = (event) => event.channel_type === "rmo" || !!event.agent_id;
const isSystemMessage = (event) => event.channel_type === "system" || event.event_type === "proactive";

const isTestText = (text) => {
    if (!text) return false;
    const normalized = text.trim().toLowerCase();
    return normalized === "teste" || normalized === "contato teste";
};

const validateJivoExport = (parsed) => {
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return "Arquivo inválido: o conteúdo não é um objeto JSON.";
    }

    const requiredKeys = ["created_at", "agents", "clients", "channels", "statuses"];
    const missing = requiredKeys.filter((k) => !(k in parsed));
    if (missing.length > 0) {
        return `Não parece um export do Jivo (faltam: ${missing.join(", ")}).`;
    }

    if (typeof parsed.created_at !== "number") {
        return "Não parece um export do Jivo (created_at inválido).";
    }
    if (!Array.isArray(parsed.agents) || !Array.isArray(parsed.clients) || !Array.isArray(parsed.channels)) {
        return "Não parece um export do Jivo (agents/clients/channels devem ser listas).";
    }

    if (parsed.clients.length === 0) {
        return null;
    }

    const sample = parsed.clients[0];
    const clientShapeOk =
        sample &&
        typeof sample === "object" &&
        "client_id" in sample &&
        Array.isArray(sample.chats);
    if (!clientShapeOk) {
        return "Não parece um export do Jivo (estrutura de clients/chats inesperada).";
    }

    const firstChat = parsed.clients
        .flatMap((c) => c.chats || [])
        .find((ch) => ch && Array.isArray(ch.events));
    if (firstChat) {
        const ev = firstChat.events[0];
        const eventShapeOk =
            !ev ||
            (typeof ev === "object" && "chat_id" in ev && "channel_type" in ev);
        if (!eventShapeOk) {
            return "Não parece um export do Jivo (estrutura de mensagens inesperada).";
        }
    }

    return null;
};

const chatTypeLabel = {
    incoming: "Atendido",
    missed: "Perdido",
    outgoing: "Saída",
};

const chatTypeClass = {
    incoming: styles.chatTypeIncoming,
    missed: styles.chatTypeMissed,
    outgoing: styles.chatTypeOutgoing,
};

const VisualizadorJsonJivo = () => {
    const dados_usuario = getCurrentUser();
    const fileInputRef = useRef(null);

    const [data, setData] = useState(null);
    const [fileName, setFileName] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterAgentId, setFilterAgentId] = useState("");
    const [filterChatType, setFilterChatType] = useState("");
    const [filterDateFrom, setFilterDateFrom] = useState("");
    const [filterDateTo, setFilterDateTo] = useState("");
    const [hideTests, setHideTests] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const processFile = (file) => {
        if (!file) return;

        const isJsonByName = /\.json$/i.test(file.name);
        const isJsonByType = file.type === "application/json" || file.type === "";
        if (!isJsonByName && !isJsonByType) {
            setErrorMessage("Apenas arquivos .json são aceitos.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            let parsed;
            try {
                parsed = JSON.parse(e.target.result);
            } catch {
                setErrorMessage("Não foi possível ler o JSON. Verifique se o arquivo é válido.");
                setData(null);
                setFileName("");
                return;
            }

            const validationError = validateJivoExport(parsed);
            if (validationError) {
                setErrorMessage(validationError);
                setData(null);
                setFileName("");
                return;
            }

            setData(parsed);
            setFileName(file.name);
            setErrorMessage("");
            setSelectedClientId(parsed.clients[0]?.client_id ?? null);
        };
        reader.onerror = () => {
            setErrorMessage("Falha ao ler o arquivo selecionado.");
        };
        reader.readAsText(file);
    };

    const handleFile = (event) => {
        processFile(event.target.files?.[0]);
        event.target.value = "";
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        if (!isDragging) setIsDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer?.files?.[0];
        processFile(file);
    };

    const clearFile = () => {
        setData(null);
        setFileName("");
        setErrorMessage("");
        setSelectedClientId(null);
        setSearchTerm("");
        setFilterAgentId("");
        setFilterChatType("");
        setFilterDateFrom("");
        setFilterDateTo("");
        setHideTests(false);
        setIsDragging(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const agentsMap = useMemo(() => {
        const map = {};
        (data?.agents || []).forEach((a) => {
            map[a.agent_id] = a.name;
        });
        return map;
    }, [data]);

    const filteredClients = useMemo(() => {
        if (!data) return [];
        const term = searchTerm.trim().toLowerCase();
        const fromSec = filterDateFrom
            ? moment.tz(filterDateFrom, TZ).startOf("day").unix()
            : null;
        const toSec = filterDateTo
            ? moment.tz(filterDateTo, TZ).endOf("day").unix()
            : null;
        const agentIdNum = filterAgentId ? parseInt(filterAgentId, 10) : null;

        const chatMatchesFilters = (chat) => {
            if (filterChatType && chat.chat_type !== filterChatType) return false;

            if (agentIdNum !== null) {
                const hasAgent =
                    chat.accepted_agent_id === agentIdNum ||
                    (chat.events || []).some((e) => e.agent_id === agentIdNum);
                if (!hasAgent) return false;
            }

            if (fromSec !== null && chat.started_at < fromSec) return false;
            if (toSec !== null && chat.started_at > toSec) return false;

            if (hideTests) {
                const allEventsAreTests = (chat.events || []).every((ev) => {
                    if (isAgentMessage(ev) || isSystemMessage(ev)) return true;
                    return isTestText(ev.text);
                });
                if (allEventsAreTests) return false;
            }

            return true;
        };

        return data.clients
            .map((client) => {
                const chats = (client.chats || []).filter(chatMatchesFilters);
                return { ...client, chats };
            })
            .filter((client) => client.chats.length > 0)
            .filter((client) => {
                if (!term) return true;
                const haystack = [
                    client.name,
                    ...(client.emails || []),
                    ...(client.phone_numbers || []),
                    client.geo?.city,
                    client.geo?.region,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return haystack.includes(term);
            })
            .sort((a, b) => {
                const lastA = a.chats[a.chats.length - 1]?.started_at || 0;
                const lastB = b.chats[b.chats.length - 1]?.started_at || 0;
                return lastB - lastA;
            });
    }, [data, searchTerm, filterAgentId, filterChatType, filterDateFrom, filterDateTo, hideTests]);

    const stats = useMemo(() => {
        if (!data) return null;
        let totalChats = 0;
        let totalMessages = 0;
        let missed = 0;
        let totalDuration = 0;
        let chatsWithDuration = 0;
        let totalWaiting = 0;
        let chatsWithWaiting = 0;
        const agentChatCount = {};

        filteredClients.forEach((client) => {
            (client.chats || []).forEach((chat) => {
                totalChats += 1;
                totalMessages += (chat.events || []).length;
                if (chat.chat_type === "missed") missed += 1;
                if (typeof chat.duration === "number" && chat.duration > 0) {
                    totalDuration += chat.duration;
                    chatsWithDuration += 1;
                }
                if (typeof chat.first_waiting === "number" && chat.first_waiting > 0) {
                    totalWaiting += chat.first_waiting;
                    chatsWithWaiting += 1;
                }
                if (chat.accepted_agent_id) {
                    agentChatCount[chat.accepted_agent_id] =
                        (agentChatCount[chat.accepted_agent_id] || 0) + 1;
                }
            });
        });

        const topAgentEntry = Object.entries(agentChatCount).sort((a, b) => b[1] - a[1])[0];
        const topAgent = topAgentEntry
            ? { name: agentsMap[topAgentEntry[0]] || `#${topAgentEntry[0]}`, count: topAgentEntry[1] }
            : null;

        return {
            totalClients: filteredClients.length,
            totalChats,
            totalMessages,
            missedRate: totalChats > 0 ? Math.round((missed / totalChats) * 100) : 0,
            avgDuration: chatsWithDuration > 0 ? Math.round(totalDuration / chatsWithDuration) : 0,
            avgWaiting: chatsWithWaiting > 0 ? Math.round(totalWaiting / chatsWithWaiting) : 0,
            topAgent,
        };
    }, [filteredClients, agentsMap, data]);

    const selectedClient = useMemo(() => {
        if (!selectedClientId) return null;
        return filteredClients.find((c) => c.client_id === selectedClientId) || null;
    }, [filteredClients, selectedClientId]);

    if (!dados_usuario?.is_superuser) {
        return <Navigate to="/sem-permissao" />;
    }

    const renderEvent = (event) => {
        const key = `${event.chat_id}-${event.msg_id}`;

        if (isSystemMessage(event)) {
            return (
                <div key={key} className={`${styles.bubbleRow} ${styles.bubbleRowSystem}`}>
                    <div className={`${styles.bubble} ${styles.bubbleSystem}`}>
                        {event.text || "(mensagem do sistema)"}
                        <div className={styles.bubbleTime}>{formatTime(event.created_ts)}</div>
                    </div>
                </div>
            );
        }

        const isAgent = isAgentMessage(event);
        const author = isAgent
            ? agentsMap[event.agent_id] || "Atendente"
            : null;

        return (
            <div
                key={key}
                className={`${styles.bubbleRow} ${
                    isAgent ? styles.bubbleRowAgent : styles.bubbleRowClient
                }`}
            >
                <div
                    className={`${styles.bubble} ${
                        isAgent ? styles.bubbleAgent : styles.bubbleClient
                    }`}
                >
                    {author && <div className={styles.bubbleAuthor}>{author}</div>}
                    {event.text || (event.media_type ? `[${event.media_type}]` : "(sem conteúdo)")}
                    <div className={styles.bubbleTime}>{formatTime(event.created_ts)}</div>
                </div>
            </div>
        );
    };

    const renderChatBlock = (chat) => {
        const agentName = chat.accepted_agent_id
            ? agentsMap[chat.accepted_agent_id] || `Atendente #${chat.accepted_agent_id}`
            : "—";

        return (
            <div key={chat.chat_id} className={styles.chatBlock}>
                <div className={styles.chatBlockHeader}>
                    <strong>Chat #{chat.chat_id}</strong>
                    <span>{formatTimestamp(chat.started_at)}</span>
                    <span>
                        <FaUserAlt size={11} /> {agentName}
                    </span>
                    <span>Espera: {formatDuration(chat.first_waiting)}</span>
                    <span>Duração: {formatDuration(chat.duration)}</span>
                    <span
                        className={`${styles.chatTypeBadge} ${
                            chatTypeClass[chat.chat_type] || ""
                        }`}
                    >
                        {chatTypeLabel[chat.chat_type] || chat.chat_type}
                    </span>
                </div>
                <div className={styles.chatBlockMessages}>
                    {(chat.events || []).length === 0 ? (
                        <div className="text-muted small">Sem mensagens registradas.</div>
                    ) : (
                        (chat.events || []).map(renderEvent)
                    )}
                </div>
            </div>
        );
    };

    return (
        <div>
            <Row className="align-items-center">
                <Col sm={12}>
                    <h4 className="page-title">Visualizador JSON Jivo</h4>
                </Col>
            </Row>

            <Card className="cardDestaque">
                <Card.Body>
                    {!data ? (
                        <>
                            <label
                                className={`${styles.uploadArea} ${
                                    isDragging ? styles.uploadAreaActive : ""
                                }`}
                                htmlFor="jivo-json-input"
                                onDragOver={handleDragOver}
                                onDragEnter={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <div className={styles.uploadIcon}>
                                    <FaUpload />
                                </div>
                                <div>
                                    <strong>
                                        {isDragging
                                            ? "Solte o arquivo para carregar"
                                            : "Arraste um arquivo JSON aqui ou clique para selecionar"}
                                    </strong>
                                </div>
                                <div className={styles.uploadHint}>
                                    Aceita o JSON exportado do Jivo. O arquivo é processado
                                    localmente no seu navegador, sem envio para o servidor.
                                </div>
                                <input
                                    id="jivo-json-input"
                                    ref={fileInputRef}
                                    type="file"
                                    accept="application/json,.json"
                                    onChange={handleFile}
                                />
                            </label>
                            {errorMessage && (
                                <div className={styles.errorBox}>{errorMessage}</div>
                            )}
                        </>
                    ) : (
                        <>
                            <Row className="align-items-center mb-3">
                                <Col>
                                    <span className={styles.fileBadge}>
                                        <FaUpload /> {fileName}
                                    </span>
                                </Col>
                                <Col className="text-end">
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={clearFile}
                                    >
                                        <FaTrash /> Remover arquivo
                                    </Button>
                                </Col>
                            </Row>

                            <Row className="g-3 mb-3">
                                <Col md={3} sm={6}>
                                    <div className={styles.statCard}>
                                        <div className={styles.statLabel}>Clientes</div>
                                        <div className={styles.statValue}>
                                            {stats?.totalClients ?? 0}
                                        </div>
                                        <div className={styles.statHint}>
                                            de {data.clients.length} no arquivo
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3} sm={6}>
                                    <div className={styles.statCard}>
                                        <div className={styles.statLabel}>Chats</div>
                                        <div className={styles.statValue}>
                                            {stats?.totalChats ?? 0}
                                        </div>
                                        <div className={styles.statHint}>
                                            {stats?.totalMessages ?? 0} mensagens
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3} sm={6}>
                                    <div className={styles.statCard}>
                                        <div className={styles.statLabel}>Perdidos</div>
                                        <div className={styles.statValue}>
                                            {stats?.missedRate ?? 0}%
                                        </div>
                                        <div className={styles.statHint}>
                                            taxa de chats perdidos
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3} sm={6}>
                                    <div className={styles.statCard}>
                                        <div className={styles.statLabel}>Tempos médios</div>
                                        <div className={styles.statValue}>
                                            {formatDuration(stats?.avgDuration || 0)}
                                        </div>
                                        <div className={styles.statHint}>
                                            espera média{" "}
                                            {formatDuration(stats?.avgWaiting || 0)}
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {stats?.topAgent && (
                                <div className="mb-3">
                                    <Badge bg="light" text="dark">
                                        Atendente mais ativo: <strong>{stats.topAgent.name}</strong>{" "}
                                        ({stats.topAgent.count} chats)
                                    </Badge>
                                </div>
                            )}

                            <Row className={`g-2 mb-3 ${styles.filterRow}`}>
                                <Col md={4} sm={12}>
                                    <Form.Group>
                                        <Form.Label className="mb-1 small">Buscar</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            placeholder="Nome, e-mail, telefone, cidade"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2} sm={6}>
                                    <Form.Group>
                                        <Form.Label className="mb-1 small">Atendente</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            value={filterAgentId}
                                            onChange={(e) => setFilterAgentId(e.target.value)}
                                        >
                                            <option value="">Todos</option>
                                            {(data.agents || []).map((a) => (
                                                <option key={a.agent_id} value={a.agent_id}>
                                                    {a.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2} sm={6}>
                                    <Form.Group>
                                        <Form.Label className="mb-1 small">Tipo</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            value={filterChatType}
                                            onChange={(e) => setFilterChatType(e.target.value)}
                                        >
                                            <option value="">Todos</option>
                                            <option value="incoming">Atendidos</option>
                                            <option value="missed">Perdidos</option>
                                            <option value="outgoing">Saída</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2} sm={6}>
                                    <Form.Group>
                                        <Form.Label className="mb-1 small">De</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="date"
                                            value={filterDateFrom}
                                            onChange={(e) => setFilterDateFrom(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2} sm={6}>
                                    <Form.Group>
                                        <Form.Label className="mb-1 small">Até</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="date"
                                            value={filterDateTo}
                                            onChange={(e) => setFilterDateTo(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Check
                                        type="switch"
                                        id="hide-tests-switch"
                                        label="Ocultar chats que contêm apenas a palavra 'teste'"
                                        checked={hideTests}
                                        onChange={(e) => setHideTests(e.target.checked)}
                                    />
                                </Col>
                            </Row>

                            <div className={styles.layout}>
                                <div className={styles.sidebar}>
                                    <div className={styles.sidebarHeader}>
                                        <strong>{filteredClients.length}</strong> cliente
                                        {filteredClients.length === 1 ? "" : "s"}
                                    </div>
                                    <div className={styles.clientList}>
                                        {filteredClients.length === 0 && (
                                            <div className="p-3 text-muted small">
                                                Nenhum cliente corresponde aos filtros.
                                            </div>
                                        )}
                                        {filteredClients.map((client) => {
                                            const lastChat = client.chats[client.chats.length - 1];
                                            const isActive = client.client_id === selectedClientId;
                                            return (
                                                <div
                                                    key={client.client_id}
                                                    className={`${styles.clientItem} ${
                                                        isActive ? styles.clientItemActive : ""
                                                    }`}
                                                    onClick={() =>
                                                        setSelectedClientId(client.client_id)
                                                    }
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            setSelectedClientId(client.client_id);
                                                        }
                                                    }}
                                                >
                                                    <strong>
                                                        {client.name || `Cliente ${client.client_id}`}
                                                    </strong>
                                                    <span className="meta">
                                                        {client.chats.length} chat
                                                        {client.chats.length === 1 ? "" : "s"} ·
                                                        último em{" "}
                                                        {formatTimestamp(lastChat?.started_at)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className={styles.chatPane}>
                                    {selectedClient ? (
                                        <>
                                            <div className={styles.chatPaneHeader}>
                                                <strong>
                                                    {selectedClient.name ||
                                                        `Cliente ${selectedClient.client_id}`}
                                                </strong>
                                                <div className="text-muted small">
                                                    {selectedClient.chats.length} chat
                                                    {selectedClient.chats.length === 1 ? "" : "s"}{" "}
                                                    · {selectedClient.visits_count || 0} visitas
                                                </div>
                                            </div>
                                            <div className={styles.clientDetails}>
                                                {selectedClient.phone_numbers?.[0] && (
                                                    <span>
                                                        <FaPhone />{" "}
                                                        {selectedClient.phone_numbers.join(", ")}
                                                    </span>
                                                )}
                                                {selectedClient.emails?.[0] && (
                                                    <span>
                                                        <FaEnvelope />{" "}
                                                        {selectedClient.emails.join(", ")}
                                                    </span>
                                                )}
                                                {selectedClient.geo && (
                                                    <span>
                                                        <FaMapMarkerAlt />{" "}
                                                        {[
                                                            selectedClient.geo.city,
                                                            selectedClient.geo.region,
                                                            selectedClient.geo.country,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" / ")}
                                                    </span>
                                                )}
                                            </div>
                                            <div className={styles.chatPaneBody}>
                                                {selectedClient.chats.map(renderChatBlock)}
                                            </div>
                                        </>
                                    ) : (
                                        <div className={styles.emptyState}>
                                            Selecione um cliente para ver os chats.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default VisualizadorJsonJivo;
